import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight, ChevronRight, ChevronDown, CheckCircle2, AlertTriangle,
  ShieldCheck, HelpCircle, Compass, CheckSquare, FileText, Camera,
  BookOpen, ClipboardList, ShieldAlert, Save, Globe2, GraduationCap,
  Users, Mail, Phone, MapPin, Heart, Baby, Sparkles,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { DVGuidesNav } from "../components/DVGuidesNav";

const beforeIcons = [
  { icon: FileText, color: "bg-teal-50 text-teal-700" },
  { icon: Users, color: "bg-sky-50 text-sky-700" },
  { icon: GraduationCap, color: "bg-violet-50 text-violet-700" },
  { icon: Globe2, color: "bg-amber-50 text-amber-700" },
  { icon: Camera, color: "bg-rose-50 text-rose-700" },
  { icon: Save, color: "bg-emerald-50 text-emerald-700" },
];

const fieldIcons = [
  FileText, Users, FileText, MapPin, Globe2, Camera,
  MapPin, Globe2, Phone, Mail, GraduationCap, Heart, Users, Baby,
];

const photoColors = [
  "bg-teal-50 border-teal-200 text-teal-700",
  "bg-sky-50 border-sky-200 text-sky-700",
  "bg-violet-50 border-violet-200 text-violet-700",
  "bg-amber-50 border-amber-200 text-amber-700",
];

export default function DVFormGuidePage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const [openFaq, setOpenFaq] = useState(null);
  const [expandedField, setExpandedField] = useState(null);

  const beforeItems = t("dvf.beforeItems", { returnObjects: true });
  const fields = t("dvf.fields", { returnObjects: true });
  const photoRules = t("dvf.photoRules", { returnObjects: true });
  const mistakes = t("dvf.mistakes", { returnObjects: true });
  const afterSteps = t("dvf.afterSteps", { returnObjects: true });
  const faqs = t("dvf.faqs", { returnObjects: true });

  return (
    <div className="min-h-screen bg-background" data-testid="dv-form-guide-page">
      <SEOHead
        title={`${t("dvf.title")} ${t("dvf.titleHighlight")}`}
        description={t("dvf.subtitle")}
      />

      {/* 1. HERO */}
      <section className="hero-gradient py-14 md:py-24" data-testid="dvf-hero">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100 mb-5 font-medium">
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
            {t("dvf.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-5">
            {t("dvf.title")}{" "}<span className="gradient-text">{t("dvf.titleHighlight")}</span>
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
            {t("dvf.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvf-hero-elig">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvf.ctaEligibility")}
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")} data-testid="dvf-hero-checklist">
              <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base">
                <CheckSquare className="mr-2 h-4 w-4" /> {t("dvf.ctaChecklist")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 2. BEFORE YOU START */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvf-before">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvf.beforeLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvf.beforeTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvf.beforeIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.isArray(beforeItems) && beforeItems.map((item, i) => {
              const meta = beforeIcons[i] || beforeIcons[0];
              return (
                <Card key={i} className="border-slate-100 shadow-sm hover:shadow-md transition-all duration-300" data-testid={`dvf-before-${i}`}>
                  <CardContent className="p-5">
                    <div className={`h-10 w-10 rounded-xl ${meta.color} flex items-center justify-center mb-3`}>
                      <meta.icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1.5">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. STEP-BY-STEP FORM INSTRUCTIONS */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvf-fields">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvf.fieldsLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvf.fieldsTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvf.fieldsIntro")}</p>
          </div>
          <div className="space-y-3">
            {Array.isArray(fields) && fields.map((f, i) => {
              const isOpen = expandedField === i;
              const Icon = fieldIcons[i] || FileText;
              return (
                <Card key={i} className="border-slate-100 shadow-sm overflow-hidden" data-testid={`dvf-field-${i}`}>
                  <button
                    className="w-full flex items-center gap-3 p-4 md:p-5 text-left hover:bg-slate-50/50 transition-colors"
                    onClick={() => setExpandedField(isOpen ? null : i)}
                    data-testid={`dvf-field-toggle-${i}`}
                  >
                    <div className="h-8 w-8 rounded-full bg-teal-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <Icon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{f.title}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5 ml-11">
                      <p className="text-sm leading-relaxed text-slate-600">{f.desc}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. PHOTO RULES */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvf-photo">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvf.photoLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvf.photoTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvf.photoIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.isArray(photoRules) && photoRules.map((r, i) => (
              <Card key={i} className={`border shadow-sm ${photoColors[i]}`} data-testid={`dvf-photo-${i}`}>
                <CardContent className="p-5">
                  <Camera className="h-6 w-6 mb-3" />
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">{r.title}</h3>
                  <p className="text-sm text-slate-600">{r.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fdocuments")}>
              <Button variant="outline" className="font-medium border-slate-200" data-testid="dvf-photo-cta">
                <Camera className="mr-2 h-4 w-4 text-teal-600" /> {t("dvf.ctaPhotoCheck")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. COMMON MISTAKES */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvf-mistakes">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvf.mistakesLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvf.mistakesTitle")}</h2>
            <p className="mt-3 text-base text-slate-600">{t("dvf.mistakesIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.isArray(mistakes) && mistakes.map((m, i) => (
              <Card key={i} className="border-amber-200/60 bg-amber-50/30 shadow-sm" data-testid={`dvf-mistake-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{m.title}</h3>
                      <p className="text-sm text-slate-600">{m.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")}>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="dvf-mistake-cta">
                <CheckSquare className="mr-2 h-4 w-4" /> {t("dvf.ctaChecklist")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. AFTER SUBMISSION */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvf-after">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvf.afterLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvf.afterTitle")}</h2>
            <p className="mt-3 text-base text-slate-600">{t("dvf.afterIntro")}</p>
          </div>
          <div className="space-y-0">
            {Array.isArray(afterSteps) && afterSteps.map((s, i) => {
              const icons = [Save, BookOpen, ShieldAlert, ShieldAlert];
              const Icon = icons[i] || Save;
              return (
                <div key={i} className="flex gap-4 md:gap-6" data-testid={`dvf-after-${i}`}>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${i === 0 ? "bg-teal-700 text-white" : i === 3 ? "bg-red-600 text-white" : "bg-slate-200 text-slate-700"}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    {i < afterSteps.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
                  </div>
                  <div className={i < afterSteps.length - 1 ? "pb-8" : ""}>
                    <h3 className="text-base font-semibold text-slate-900 mb-1">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTEXTUAL LINKS */}
      <section className="py-10 md:py-14 bg-white" data-testid="dvf-contextual">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4 text-center">{t("dvNav.label")}</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Link to={lp("/dv-lottery-photo-requirements")} data-testid="dvf-ctx-photo">
              <Button variant="outline" size="sm" className="font-medium border-slate-200 text-xs px-3 py-2 h-auto">
                <Camera className="mr-1.5 h-3.5 w-3.5 text-teal-600" /> {t("dvNav.guides.2.title")}
              </Button>
            </Link>
            <Link to={lp("/dv-lottery-common-mistakes")} data-testid="dvf-ctx-mistakes">
              <Button variant="outline" size="sm" className="font-medium border-slate-200 text-xs px-3 py-2 h-auto">
                <AlertTriangle className="mr-1.5 h-3.5 w-3.5 text-teal-600" /> {t("dvNav.guides.4.title")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <DVGuidesNav currentPath="/how-to-fill-dv-lottery-form" />

      {/* 7. FAQ */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvf-faq">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvf.faqLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvf.faqTitle")}</h2>
          </div>
          <div className="space-y-3">
            {Array.isArray(faqs) && faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="border-slate-100 shadow-sm overflow-hidden" data-testid={`dvf-faq-${i}`}>
                  <button
                    className="w-full flex items-start gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    data-testid={`dvf-faq-toggle-${i}`}
                  >
                    <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600 ml-8">{faq.a}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="py-14 md:py-24 bg-teal-700" data-testid="dvf-final-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">{t("dvf.finalTitle")}</h2>
          <p className="text-base text-teal-100 mb-8 max-w-xl mx-auto">{t("dvf.finalSubtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvf-final-elig">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg w-full sm:w-auto px-6 py-6 text-base">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvf.ctaFinalEligibility")}
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register")} data-testid="dvf-final-start">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-6 py-6 text-base">
                {t("dvf.ctaFinalStart")} <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")} data-testid="dvf-final-checklist">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-6 py-6 text-base">
                <CheckSquare className="mr-2 h-4 w-4" /> {t("dvf.ctaFinalChecklist")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
